import { type MutableRefObject, useCallback } from 'react';

import type { Editor as TiptapEditor } from '@tiptap/core';

import { useAlert, useCopyToClipboard } from '@spotify/confidence-core-react';

import { useCollaborativeDocumentContext } from '../../contexts';
import { getDocumentMarkdown } from '../../utils';

export const useCopyDocumentMarkdownValue = (
  editorRef?: MutableRefObject<TiptapEditor | null>,
) => {
  const { collaborativeDocument } = useCollaborativeDocumentContext();

  return useCallback(
    () =>
      getDocumentMarkdown({
        editor: editorRef?.current,
        markdown: collaborativeDocument?.markdown,
        content: collaborativeDocument?.content,
      }) ?? '',
    [
      editorRef,
      collaborativeDocument?.markdown,
      collaborativeDocument?.content,
    ],
  );
};

export const useCopyAsMarkdown = (
  editorRef?: MutableRefObject<TiptapEditor | null>,
) => {
  const alert = useAlert();
  const { copy } = useCopyToClipboard();
  const getValue = useCopyDocumentMarkdownValue(editorRef);

  const copyAsMarkdown = useCallback(async (): Promise<void> => {
    const markdown = getValue();

    if (!markdown) {
      alert.post({
        message: 'Could not copy to clipboard',
        severity: 'error',
        display: 'transient',
      });
      return;
    }

    const didCopy = await copy(markdown);
    alert.post(
      didCopy
        ? {
            message: 'Copied to clipboard',
            severity: 'success',
            display: 'transient',
          }
        : {
            message: 'Could not copy to clipboard',
            severity: 'error',
            display: 'transient',
          },
    );
  }, [alert, copy, getValue]);

  return { copyAsMarkdown, getValue };
};
